import { delay, select, call, put } from 'redux-saga/effects';
import { stringify } from 'query-string';
import moment from 'moment';
import { getErrorMessage } from '../../services/Errors';
import ApplicationActions from '../actions/ApplicationActions';
import {
  isNew,
  getData,
  getCurrentPage,
  validateField,
  getValidators,
  getRegexes,
  getFilters,
  getPage,
  getPerPage,
  getApplicationId,
} from '../reducers/ApplicationReducer';
import { getLanguage } from '../reducers/AppReducer';
import { isLAO } from '../reducers/LoginReducer';
import { callApi } from './Api';
import { getConfig, getVisaTypes } from '../reducers/ConfigReducer';
import { createDownloadLinkFromBlob } from '../../utils/createDownloadLinkFromBlob';

const {
  getApplicationsSuccess,
  getApplicationsFailure,
  getApplicationSuccess,
  getApplicationFailure,
  getUsersSuccess,
  getUsersFailure,
  saveSuccess,
  saveFailure,
  saveRequest,
  submitSuccess,
  submitFailure,
  validateSuccess,
  uploadSuccess,
  uploadFailure,
  deleteDocumentSuccess,
  deleteDocumentFailure,
  sendMessageSuccess,
  sendMessageFailure,
  sendMessageToApplicantSuccess,
  sendMessageToApplicantFailure,
  clearMessageSent,
  setTotal,
  successCheckZeroPayment,
  failureCheckZeroPayment,
  downloadApplicationSuccess,
  downloadApplicationFailure,
} = ApplicationActions;

const idsOnPage = {
  1: ['visatypeId', 'visapurposeId', 'nationalityCurrentId'],
  2: [
    'nameLast',
    'nameFirst',
    'dob',
    'sex',
    'nationalityOriginId',
    'pob',
    'relStatus',
    'occupationId',
    'noChildren',
  ],
  3: [
    'phonenumber',
    'applicantEmail',
    'street',
    'city',
    'state',
    'zip',
    'countryId',
    'ecName',
    'ecPhonenumber',
    'ecEmail',
  ],
  4: [
    'passportNo',
    'passportPoi',
    'passportExpireAt',
    'passportDoi',
    'passportTypeId',
    'poe',
    'arrivalAt',
    'lengthStay',
    'destList',
    'destTranspmode',
    'destAddress',
  ],
};
const idsOnPageWithDiplomatic = {
  1: [
    'visatypeId',
    'visapurposeId',
    'nationalityCurrentId',
    'diplomaticMissionUuid',
  ],
  2: [
    'nameLast',
    'nameFirst',
    'dob',
    'sex',
    'nationalityOriginId',
    'pob',
    'relStatus',
    'occupationId',
    'noChildren',
  ],
  3: [
    'phonenumber',
    'applicantEmail',
    'street',
    'city',
    'state',
    'zip',
    'countryId',
    'ecName',
    'ecPhonenumber',
    'ecEmail',
  ],
  4: [
    'passportNo',
    'passportPoi',
    'passportExpireAt',
    'passportDoi',
    'passportTypeId',
    'poe',
    'arrivalAt',
    'lengthStay',
    'destList',
    'destTranspmode',
    'destAddress',
  ],
};

function toDateTime(type, date) {
  return date
    ? moment(`${date}T${type === 'from' ? '00:00:00' : '23:59:59'}`).format(
        'YYYY-MM-DDTHH:mm:ss'
      ) + 'Z'
    : '';
}

function makeFilters(filters, page, size) {
  let result = {};
  result.page = page;
  result.size = size;
  for (let key in filters) {
    if (key === 'shortCode' || key === 'nameLast') {
      result[`${key}.contains`] = filters[key];
    } else if (
      key === 'status' ||
      key === 'createdByAssignmentId' ||
      key === 'isMarkedForLocalPayment'
    ) {
      result[`${key}.equals`] = filters[key];
    } else if (key === 'submittedAt') {
      let [from, to] = filters[key].split(' ');
      result[`${key}.greaterOrEqualThan`] = toDateTime('from', from);
      result[`${key}.lessOrEqualThan`] = toDateTime('to', to);
    } else if (key === 'submitterAuthorityId') {
      result[`${key}.specified`] = Boolean(filters[key]);
    }
  }
  return `?${stringify(result, { encode: false })}`;
}

export function* getApplications(api) {
  let filters = yield select(getFilters);
  let page = yield select(getPage);
  let perPage = yield select(getPerPage);
  let response = yield call(
    callApi,
    api,
    'getApplications',
    makeFilters(filters, page, perPage)
  );

  if (response.ok) {
    yield put(getApplicationsSuccess(response.data));
    yield put(setTotal(response.headers['x-total-count']));
  } else {
    yield put(
      getApplicationsFailure(
        getErrorMessage(response, 'Could not load applications.')
      )
    );
  }
}

export function* getApplication(api, { uuid }) {
  try {
    let response = yield call(callApi, api, 'getApplication', uuid);
    if (response.ok) {
      yield put(getApplicationSuccess(response.data));
    } else {
      yield put(
        getApplicationFailure(
          getErrorMessage(response, 'Could not load application.')
        )
      );
    }
  } catch (e) {
    console.log(e);
    yield put(getApplicationFailure(e));
  }
}

export function* getUsers(api) {
  let response = yield call(callApi, api, 'getUsers');
  if (response.ok) {
    yield put(getUsersSuccess(response.data));
  } else {
    yield put(
      getUsersFailure(getErrorMessage(response, 'Could not load applications.'))
    );
  }
}

export function* saveApplication(api, { history }) {
  let shouldCreate = yield select(isNew);
  let data = yield select(getData);
  let isLao = yield select(isLAO);
  if (data.applicantEmail === null && !isLao) {
    data = data.set('applicantEmail', data.email);
  }
  let applicantLanguage = yield select(getLanguage);
  let currentPage = yield select(getCurrentPage);
  let response = yield call(
    callApi,
    api,
    shouldCreate ? 'createApplication' : 'saveApplication',
    {
      ...data,
      channel: 'web',
      applicantLanguage,
    }
  );
  if (response.ok) {
    yield put(saveSuccess(response.data));
    if (currentPage === 4) yield call(history.push, '/documents');
  } else {
    yield put(
      saveFailure(getErrorMessage(response, 'Could not save application.'))
    );
  }
}

export function* submitApplication(api, { uuid }) {
  let applicationId = yield select(getApplicationId);
  let response;
  try {
    response = yield call(callApi, api, 'submitApplication', { uuid });

    let responseZero = yield call(
      callApi,
      api,
      'checkZeroPayment',
      applicationId
    );
    if (responseZero.ok) {
      yield put(successCheckZeroPayment(responseZero.data));
    } else {
      yield put(
        failureCheckZeroPayment(
          getErrorMessage(response, 'Could not check iz payment equal to zero.')
        )
      );
    }

    if (response && response.ok) {
      yield put(submitSuccess(response.data));
    } else {
      yield put(
        submitFailure(
          getErrorMessage(response, 'Could not submit application.')
        )
      );
    }
  } catch (e) {
    yield put(submitFailure(e.message));
  }
}

export function* validateApplication({ history }) {
  if (process.env.REACT_APP_IGNORE_VALIDATIONS === 'true') {
    yield put(validateSuccess({}));
    if (history) yield put(saveRequest(history));
    return;
  }
  let errors = {};
  let page = yield select(getCurrentPage);
  let data = yield select(getData);
  let config = yield select(getConfig);
  let validators = yield select(getValidators);
  let regexes = yield select(getRegexes);
  let lang = yield select(getLanguage);
  let hasError = false;
  const visaTypeSelected =
    data && data.visatypeId
      ? config.visaTypes.find(i => i.uuid === data.visatypeId)
      : null;
  let isDiplomaticMission =
    visaTypeSelected && visaTypeSelected.visaTypeConfig.isDiplomaticMission;
  let relevantIds =
    !isDiplomaticMission || !data.visatypeId
      ? idsOnPage[page]
      : idsOnPageWithDiplomatic[page];
  for (let index = 0; index < relevantIds.length; index++) {
    let id = relevantIds[index];
    let error = yield call(
      validateField,
      data[id],
      validators[id],
      regexes[id],
      id,
      lang
    );
    if (typeof error !== 'undefined') {
      hasError = true;
      errors[id] = error;
    }
  }
  yield put(validateSuccess(errors));
  if (!hasError && history) {
    yield put(saveRequest(history));
  }
}

export function* uploadDocument(api, { file, documentTypeId, docUuid }) {
  let applicationId = yield select(getApplicationId);
  let response = yield call(
    callApi,
    api,
    'uploadDocument',
    applicationId,
    file,
    documentTypeId,
    docUuid
  );
  if (response.ok) {
    yield put(uploadSuccess(response.data));
  } else {
    yield put(
      uploadFailure(getErrorMessage(response, 'Failed to upload the document.'))
    );
  }
}

export function* deleteDocumentSaga(api, { documentTypeId, docUuid }) {
  const applicationId = yield select(getApplicationId);
  const response = yield call(
    callApi,
    api,
    'deleteDocument',
    applicationId,
    documentTypeId,
    docUuid
  );
  if (response.ok) {
    yield put(deleteDocumentSuccess(response.data));
  } else {
    yield put(
      deleteDocumentFailure(
        getErrorMessage(response, 'Failed to upload the document.')
      )
    );
  }
}

export function* sendMessage(api, { message, file, documentTypeId }) {
  let applicationId = yield select(getApplicationId);
  let uploadedDoc;
  if (file) {
    // first upload the document
    let response = yield call(
      callApi,
      api,
      'uploadDocument',
      applicationId,
      file,
      documentTypeId
    );
    if (response.ok) {
      uploadedDoc = response.data;
    } else {
      yield put(
        sendMessageFailure(
          getErrorMessage(response, 'Failed to upload the document.')
        )
      );
      return;
    }
  }
  let documentIds = uploadedDoc ? [uploadedDoc.uuid] : [];
  let response = yield call(
    callApi,
    api,
    'respondToAgent',
    applicationId,
    message,
    documentIds
  );
  if (response.ok) {
    yield put(sendMessageSuccess(response.data));
  } else {
    yield put(
      sendMessageFailure(getErrorMessage(response, 'Could not send message.'))
    );
  }
}

export function* sendMessageToApplicant(api, { message }) {
  let applicationId = yield select(getApplicationId);
  let response = yield call(
    callApi,
    api,
    'messageToApplicant',
    applicationId,
    message
  );
  if (response.ok) {
    yield put(sendMessageToApplicantSuccess());
    yield delay(5e3);
    yield put(clearMessageSent());
    // yield call(history.push, '/applications');
  } else {
    yield put(
      sendMessageToApplicantFailure(
        getErrorMessage(response, 'Could not send message.')
      )
    );
  }
}

export function* downloadApplicationSaga(api) {
  let applicationId = yield select(getApplicationId);
  const response = yield call(
    callApi,
    api,
    'downloadApplication',
    applicationId
  );
  if (response.ok) {
    yield put(downloadApplicationSuccess());
    createDownloadLinkFromBlob(response.data, 'Application Summary PDF');
  } else {
    yield put(
      downloadApplicationFailure(
        getErrorMessage(
          response,
          'Could not download the Application Summary PDF.'
        )
      )
    );
  }
}



// WEBPACK FOOTER //
// ./src/state/sagas/ApplicationSagas.js